import { TaskStatus, TaskType } from '@monorepo/shared';

export interface TaskStatusDisplay {
  label: string;
  dotClassName: string;
  textClassName: string;
}

export const taskStatusDisplay: Record<TaskStatus, TaskStatusDisplay> = {
  [TaskStatus.NOT_STARTED]: {
    label: 'tasks.status.not_started',
    dotClassName: 'bg-gray-400',
    textClassName: 'text-gray-600',
  },
  [TaskStatus.DUE_SOON]: {
    label: 'tasks.status.due_soon',
    dotClassName: 'bg-orange-500',
    textClassName: 'text-orange-700',
  },
  [TaskStatus.OVERDUE]: {
    label: 'tasks.status.overdue',
    dotClassName: 'bg-red-500',
    textClassName: 'text-red-700',
  },
  [TaskStatus.COMPLETED]: {
    label: 'tasks.status.completed',
    dotClassName: 'bg-green-500',
    textClassName: 'text-green-700',
  },
  [TaskStatus.REJECTED]: {
    label: 'tasks.status.rejected',
    dotClassName: 'bg-red-500',
    textClassName: 'text-red-700',
  },
};

export const taskTypeLabel: Record<TaskType, string> = {
  [TaskType.VALIDATION]: 'tasks.type.validation',
  [TaskType.DIFFUSION]: 'tasks.type.diffusion',
  [TaskType.READ]: 'tasks.type.read',
};

export const formatDueDate = (dueDate: Date | string | null): string => {
  if (!dueDate) {
    return '—';
  }
  const date = new Date(dueDate);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const isActionableStatus = (status: TaskStatus): boolean =>
  status === TaskStatus.NOT_STARTED ||
  status === TaskStatus.DUE_SOON ||
  status === TaskStatus.OVERDUE;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface DueDateDelay {
  key: string;
  count: number;
  className: string;
}

export const getDueDateDelay = (
  dueDate: Date | string | null,
  status: TaskStatus,
  now: Date = new Date(),
): DueDateDelay | null => {
  if (!dueDate || status === TaskStatus.COMPLETED || status === TaskStatus.REJECTED) {
    return null;
  }

  const endOfDueDate = new Date(dueDate);
  if (Number.isNaN(endOfDueDate.getTime())) {
    return null;
  }
  endOfDueDate.setUTCHours(23, 59, 59, 999);

  const days = Math.ceil((endOfDueDate.getTime() - now.getTime()) / MS_PER_DAY) - 1;

  if (days < 0) {
    return {
      key: 'tasks.due_date.overdue',
      count: Math.abs(days),
      className: 'text-red-600',
    };
  }

  if (days === 0) {
    return { key: 'tasks.due_date.today', count: 0, className: 'text-orange-600' };
  }

  return {
    key: 'tasks.due_date.remaining',
    count: days,
    className: days <= 7 ? 'text-orange-600' : 'text-gray-500',
  };
};

export const formatCompletionDate = (completedAt: Date | string | null): string | null => {
  if (!completedAt) {
    return null;
  }

  const date = new Date(completedAt);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};
